import React, { useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { getMenuLabel } from '../lib/menu'
import MenuIcon from './MenuIcon'

const USER_BLOCK_IDS = new Set(['dashboard', 'sales', 'customer', 'design', 'work', 'admin'])

function isPathActive(pathname, itemPath) {
  if (!itemPath) return false
  if (itemPath === '/') return pathname === '/'
  return pathname === itemPath || pathname.startsWith(`${itemPath}/`)
}

function splitOverviewBlocks(blocks) {
  const userBlocks = []
  const operatorBlocks = []
  ;(blocks || []).forEach((block) => {
    if (USER_BLOCK_IDS.has(block.id)) userBlocks.push(block)
    else operatorBlocks.push(block)
  })
  return { userBlocks, operatorBlocks }
}

function MenuOverviewLink({ item, pathname, onItemClick }) {
  const active = isPathActive(pathname, item.path)
  return (
    <Link
      to={item.path}
      onClick={onItemClick}
      className={`flex items-center gap-3 px-4 py-3 text-base font-medium transition-colors ${
        active
          ? 'bg-sky-50 text-slate-950'
          : 'text-slate-800 hover:bg-slate-50'
      }`}
    >
      <MenuIcon menuItemId={item.id} emoji={item.icon} className={active ? 'text-brand-navy' : 'text-slate-500'} size={20} />
      <span className="min-w-0 leading-snug">{getMenuLabel(item)}</span>
    </Link>
  )
}

function MenuOverviewColumn({ block, pathname, onItemClick }) {
  return (
    <section className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white">
      <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 text-base font-bold text-slate-950">
        {block.label}
      </div>
      <ul className="divide-y divide-slate-100">
        {block.items.map((item) => (
          <li key={item.id}>
            <MenuOverviewLink item={item} pathname={pathname} onItemClick={onItemClick} />
          </li>
        ))}
      </ul>
    </section>
  )
}

function MenuOverviewColumnGrid({ blocks, pathname, onItemClick, columnsClass = 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' }) {
  if (!blocks.length) return null
  return (
    <div className={`grid gap-4 ${columnsClass}`}>
      {blocks.map((block) => (
        <MenuOverviewColumn
          key={block.id}
          block={block}
          pathname={pathname}
          onItemClick={onItemClick}
        />
      ))}
    </div>
  )
}

export default function MenuOverviewList({ blocks, onItemClick }) {
  const { pathname } = useLocation()
  const { userBlocks, operatorBlocks } = useMemo(() => splitOverviewBlocks(blocks), [blocks])

  if (!userBlocks.length && !operatorBlocks.length) {
    return (
      <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-12 text-center text-sm text-slate-500">
        표시할 메뉴가 없습니다.
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <MenuOverviewColumnGrid
        blocks={userBlocks}
        pathname={pathname}
        onItemClick={onItemClick}
        columnsClass="grid-cols-1 sm:grid-cols-2 xl:grid-cols-3"
      />

      {operatorBlocks.length ? (
        <div className="space-y-4">
          <h2 className="text-sm font-bold uppercase tracking-wide text-brand-navy">운영자 메뉴</h2>
          <MenuOverviewColumnGrid
            blocks={operatorBlocks}
            pathname={pathname}
            onItemClick={onItemClick}
            columnsClass="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          />
        </div>
      ) : null}
    </div>
  )
}
